import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { apiService, Schema } from '../../services/api';
import GuidDisplay from './GuidDisplay';
import SchemaDetailDrawer from './SchemaDetailDrawer';
import UnifiedSchemaBrowser from '../schema-view/UnifiedSchemaBrowser';

/* ---------- Schema member detail (fetches definition by ID) ----------
 *
 * Used by callers that have only a schema id and want a quick inline
 * preview (e.g., expanded rows in a list). Renders into a Box, not a
 * drawer — used embedded in other UI. */

interface SchemaMemberDetailProps {
  schemaId: string;
}

const SchemaMemberDetail: React.FC<SchemaMemberDetailProps> = ({ schemaId }) => {
  const [schema, setSchema] = useState<Schema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getSchema(schemaId)
      .then((data) => {
        if (!cancelled) setSchema(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Failed to load schema');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [schemaId]);

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !schema) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" variant="outlined">
          {error || 'Schema not found.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          {schema.schema_name}
        </Typography>
        <Chip label={`v${schema.version}`} size="small" variant="outlined" />
        <Chip label={schema.status} size="small" color={schema.status === 'active' ? 'success' : 'default'} />
      </Stack>
      <UnifiedSchemaBrowser definition={(schema.definition || {}) as any} />
    </Box>
  );
};

/* ---------- Main drawer ---------- */

interface SchemaPreviewDialogProps {
  open: boolean;
  schema: Schema | null;
  onClose: () => void;
}

/**
 * Schema preview drawer. Right-anchored MUI Drawer (was a centered
 * Dialog) — uses ~92% of the viewport so the tree + level detail +
 * inline catalog have enough horizontal room without feeling like a
 * page-within-a-page.
 *
 * The exported component name and props are unchanged so existing
 * callers don't need updating.
 */
const SchemaPreviewDialog: React.FC<SchemaPreviewDialogProps> = ({ open, schema, onClose }) => {
  if (!schema) return null;

  const title = (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="h6" fontWeight={600} noWrap>
        {schema.schema_name}
      </Typography>
      <Chip label={`v${schema.version}`} size="small" variant="outlined" />
      <Chip
        label={schema.status}
        size="small"
        color={schema.status === 'active' ? 'success' : 'default'}
      />
      <Typography variant="caption" color="text.secondary">
        <GuidDisplay guid={schema.schema_id} />
      </Typography>
    </Stack>
  );

  return (
    <SchemaDetailDrawer
      open={open}
      onClose={onClose}
      title={title}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <UnifiedSchemaBrowser definition={(schema.definition || {}) as any} />
    </SchemaDetailDrawer>
  );
};

export { SchemaMemberDetail };
export default SchemaPreviewDialog;
